import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Text,
  HStack,
  VStack,
  Link,
  Flex,
  UnorderedList,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Textarea,
  Button,
  useDisclosure,
  useToast,
  Spinner,
  Portal,
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, ExternalLink, Users, DollarSign, MapPin, Building2, Layers, Plus, Search, Pencil, Trash2, GripVertical, Sparkles, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatEmployees } from '../../utils/tableUtils';
import { mergeDetailViewFields } from '../../utils/detailViewCustomFields';
import DetailViewFieldsList from './DetailViewFieldsList';
import api from '../../services/api';

// Custom LinkedIn Icon
const LinkedInIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// 6-dot grip handle component
const DragHandle = ({ orientation = 'vertical', ...props }) => (
  <Flex
    align="center"
    justify="center"
    cursor="grab"
    color="whiteAlpha.300"
    _hover={{ color: 'whiteAlpha.600' }}
    transition="color 0.12s ease"
    flexShrink={0}
    {...props}
  >
    <GripVertical size={14} strokeWidth={1.5} style={orientation === 'horizontal' ? { transform: 'rotate(90deg)' } : undefined} />
  </Flex>
);

const _URL_RE = /^https?:\/\/\S+$/i;
const _MD_LINK_RE = /\[.*?\]\(.*?\)/;

const _renderInlineVal = (val) => {
  if (val === null || val === undefined) return '—';
  const str = String(val);
  if (_URL_RE.test(str.trim())) {
    return (
      <Link
        href={str.trim()}
        isExternal
        color="blue.400"
        _hover={{ opacity: 0.8 }}
        onClick={(e) => e.stopPropagation()}
      >
        {str.trim().replace(/^https?:\/\/(www\.)?/, '')}
      </Link>
    );
  }
  if (_MD_LINK_RE.test(str)) {
    return (
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => <Text as="span" {...props} />,
          a: ({ node, ...props }) => (
            <Link
              {...props}
              color="blue.400"
              textDecoration="none"
              _hover={{ textDecoration: 'underline', color: 'blue.300' }}
              isExternal
              onClick={(e) => e.stopPropagation()}
            />
          ),
        }}
      >
        {str}
      </ReactMarkdown>
    );
  }
  return str;
};

const renderFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return (
      <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7" fontWeight="400">—</Text>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
    }
    return (
      <UnorderedList spacing={1} ml={5} mt={1}>
        {value.map((item, idx) => (
          <ListItem key={idx} fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" fontWeight="400">
            {_renderInlineVal(item)}
          </ListItem>
        ))}
      </UnorderedList>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
    }
    return (
      <VStack align="start" spacing={1} mt={1}>
        {entries.map(([key, val]) => (
          <Text key={key} fontSize="sm" lineHeight="1.7">
            <Text as="span" fontWeight="600" color="white">{key}:</Text>{' '}
            <Text as="span" color="whiteAlpha.800">{_renderInlineVal(val)}</Text>
          </Text>
        ))}
      </VStack>
    );
  }

  const str = String(value);
  const trimmed = str.trim();
  if (!trimmed) {
    return <Text fontSize="sm" color="whiteAlpha.400" lineHeight="1.7">—</Text>;
  }
  if (_URL_RE.test(trimmed)) {
    return (
      <Link
        href={trimmed}
        isExternal
        fontSize="sm"
        color="blue.400"
        fontWeight="400"
        lineHeight="1.7"
        _hover={{ opacity: 0.8 }}
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed.replace(/^https?:\/\/(www\.)?/, '')}
      </Link>
    );
  }
  if (_MD_LINK_RE.test(str)) {
    return (
      <Box
        fontSize="sm"
        color="whiteAlpha.800"
        lineHeight="1.7"
        fontWeight="400"
        sx={{ '& p': { margin: 0, whiteSpace: 'pre-wrap' }, '& p + p': { marginTop: '0.75em' } }}
      >
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => (
              <Text as="p" fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" fontWeight="400" mb={0} whiteSpace="pre-wrap" {...props} />
            ),
            a: ({ node, ...props }) => (
              <Link
                {...props}
                color="blue.400"
                textDecoration="none"
                _hover={{ textDecoration: 'underline', color: 'blue.300' }}
                isExternal
                onClick={(e) => e.stopPropagation()}
              />
            ),
          }}
        >
          {str}
        </ReactMarkdown>
      </Box>
    );
  }
  return (
    <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" whiteSpace="pre-wrap" fontWeight="400">
      {str}
    </Text>
  );
};

/**
 * Notion-style company detail view
 * Replaces the table when a company row is clicked
 */
const CompanyDetailView = ({ company, onBack, customFields, onFieldsChange, campaignId, onPrev, onNext, currentIndex, totalCount }) => {
  // Modal for adding fields
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [fieldName, setFieldName] = useState('');
  const [fieldDescription, setFieldDescription] = useState('');
  const [modalMode, setModalMode] = useState('quick');
  const [customPrompt, setCustomPrompt] = useState('');
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);
  // 'horizontal' or 'vertical' — controls where the new field lands

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { fieldId, source, label, valueCount }
  const [isDeleting, setIsDeleting] = useState(false);

  const [detailFields, setDetailFields] = useState([]);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);

  // Convert any raw value (boolean, array, object, string) to a display string.
  // Also handles legacy string-encoded types stored before native-type enforcement.
  const isUrl = (str) => {
    try {
      const url = new URL(str);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return 'N/A';
      return value.map(item =>
        item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item)
      ).join(', ');
    }
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    // String — handle legacy string-encoded booleans/arrays from before native-type storage
    const trimmed = value.trim();
    if (trimmed === 'True' || trimmed === 'true') return 'true';
    if (trimmed === 'False' || trimmed === 'false') return 'false';
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'boolean') return String(parsed);
      if (typeof parsed === 'number') return String(parsed);
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return 'N/A';
        return parsed.map(item =>
          item !== null && typeof item === 'object' ? JSON.stringify(item) : String(item)
        ).join(', ');
      }
      if (parsed !== null && typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
    } catch {}
    return trimmed;
  };

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { fieldId, source: 'h'|'v', x, y }
  const [renamingFieldId, setRenamingFieldId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const contextMenuRef = useRef(null);
  const renameInputRef = useRef(null);

  // Drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  // Focus rename input
  useEffect(() => {
    if (renamingFieldId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingFieldId]);


  // Research DB fields + all other campaign custom fields with values on this company.
  useEffect(() => {
    if (!campaignId || !company) return;
    const membershipId = company.membership_id || company._id;
    if (!membershipId) return;

    let cancelled = false;
    setFieldsLoaded(false);

    api.get(`/api/research/campaigns/${campaignId}/companies/${membershipId}/fields`)
      .then(({ data }) => {
        if (cancelled) return;
        const merged = mergeDetailViewFields({
          apiFields: data.fields || [],
          fieldDefinitions: customFields,
          entityCustomFields: company.custom_fields,
        });
        setDetailFields(merged);
        setFieldsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load saved fields:', err);
        setFieldsLoaded(true);
      });

    return () => { cancelled = true; };
  }, [campaignId, company, customFields]);

  // Report field changes to parent (for "Run for All" button) — only after initial load
  useEffect(() => {
    if (onFieldsChange && fieldsLoaded) {
      const allFields = detailFields.map(f => ({
        id: f.id,
        label: f.label,
        description: f.description,
        config_id: f.config_id || null,
      }));
      onFieldsChange(allFields);
    }
  }, [detailFields, onFieldsChange, fieldsLoaded]);

  if (!company) return null;

  const handleDeleteField = async (fieldId) => {
    setContextMenu(null);
    const field = detailFields.find(f => f.id === fieldId);
    const label = field?.label || 'this field';

    try {
      const { data } = await api.get(`/api/research/fields/${fieldId}/value-count`);
      const count = data.value_count || 0;

      if (count > 1) {
        setDeleteConfirm({ fieldId, label, valueCount: count });
        return;
      }

      await executeDelete(fieldId);
    } catch {
      await executeDelete(fieldId);
    }
  };

  const executeDelete = async (fieldId) => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/research/fields/${fieldId}`);
      setDetailFields(prev => prev.filter(f => f.id !== fieldId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete failed:', err);
      toast({
        title: 'Failed to delete field',
        description: err?.response?.data?.detail || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartRename = (field) => {
    setRenamingFieldId(field.id);
    setRenameValue(field.label);
    setContextMenu(null);
  };

  const handleFinishRename = async (fieldId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingFieldId(null);
      setRenameValue('');
      return;
    }

    setDetailFields(prev =>
      prev.map(f => f.id === fieldId ? { ...f, label: trimmed } : f)
    );
    setRenamingFieldId(null);
    setRenameValue('');

    try {
      await api.patch(`/api/research/fields/${fieldId}/rename`, { display_name: trimmed });
    } catch (err) {
      console.error('Rename failed:', err);
      toast({
        title: 'Failed to rename field',
        description: err?.response?.data?.detail || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => {
    if (dragIdx !== null && dragOverIdx !== null && dragIdx !== dragOverIdx) {
      setDetailFields(prev => {
        const arr = [...prev];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(dragOverIdx, 0, moved);
        return arr;
      });
    }
    setDragIdx(null);
    setDragOverIdx(null);
  };

  // --- Data ---
  const location = company.city && company.state
    ? `${company.city}, ${company.state}`
    : company.headquarters || null;

  const metaItems = [
    { icon: Layers, label: 'Industry', value: company.industry || company.sub_industry || null },
    { icon: Users, label: 'Headcount', value: company.employees ? formatEmployees(company.employees) : null },
    { icon: DollarSign, label: 'Revenue', value: company.revenue_millions ? formatCurrency(company.revenue_millions) : null },
    { icon: MapPin, label: 'Location', value: location },
  ].filter(item => item.value);

  const linkItems = [];
  if (company.website) {
    linkItems.push({
      icon: ExternalLink, label: 'Website',
      href: company.website.startsWith('http') ? company.website : `https://${company.website}`,
      color: 'blue.400',
    });
  }
  if (company.linkedin_url) {
    linkItems.push({
      iconComponent: LinkedInIcon, label: 'LinkedIn',
      href: company.linkedin_url, color: '#0077B5',
    });
  }

  // --- Add field handler — calls backend, populates result ---
  const handleAddField = async () => {
    if (!fieldName.trim()) return;

    const label = fieldName.trim();
    const description = fieldDescription.trim();
    const fieldId = `dynamic_${Date.now()}`;

    const newField = { id: fieldId, label, description, value: null, loading: true };
    setDetailFields(prev => [...prev, newField]);

    setFieldName('');
    setFieldDescription('');
    onClose();

    try {
      const { data } = await api.post(
        `/api/research/campaigns/${campaignId}/deep-dive`,
        {
          prompt: `${label}: ${description}`,
          entity_type: 'company',
          entity_name: company.company_name || '',
          company_website: company.website || '',
          membership_id: company.membership_id || company._id || '',
          max_fields: 1,
          display_name_override: label,
        },
      );
      const field = data.fields?.[0];
      if (!field) return;

      const realId = field.id || fieldId;
      const configId = data.config_id;

      // Replace the placeholder id with the real DB id
      setDetailFields(prev =>
        prev.map(f =>
          f.id === fieldId
            ? {
                ...f,
                id: realId,
                config_id: configId,
                value: field.value ?? 'No data found',
                loading: false,
              }
            : f
        )
      );
    } catch (err) {
      console.error('Research failed:', err);
      setDetailFields(prev =>
        prev.map(f => f.id === fieldId ? { ...f, value: 'Research failed — try again', loading: false } : f)
      );
    }
  };


  // --- Deep Dive handler — LLM determines fields, OpenAI runs in background ---
  const handleDeepDive = async () => {
    if (!customPrompt.trim()) return;

    const prompt = customPrompt.trim();
    setDeepDiveLoading(true);

    try {
      const { data } = await api.post(
        `/api/research/campaigns/${campaignId}/deep-dive`,
        {
          prompt,
          entity_type: 'company',
          entity_name: company.company_name || '',
          company_website: company.website || '',
          membership_id: company.membership_id || company._id || '',
        },
      );

      // Response contains resolved values — populate fields immediately
      const returnedFields = (data.fields || []).map(f => ({
        id: f.id,
        label: f.label,
        description: f.description || '',
        value: f.value ?? '',
        loading: false,
        config_id: data.config_id || null,
      }));
      const incomingIds = new Set(returnedFields.map(f => f.id));

      setDetailFields(prev => [
        ...prev.filter(f => !incomingIds.has(f.id)),
        ...returnedFields,
      ]);

      setCustomPrompt('');
      onClose();
    } catch (err) {
      console.error('Deep dive failed:', err);
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const openAddFieldModal = () => {
    onOpen();
  };

  return (
    <Box
      h="100%"
      position="relative"
      bg="#0a0a0a"
    >
      <Box
        h="100%"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.08)', borderRadius: '3px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.15)' },
        }}
      >
      <Box maxW="820px" mx="auto" px={8} py={6}>
        {/* Counter */}
        {totalCount > 0 && (
          <Flex justify="flex-end" mb={4}>
            <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">
              {currentIndex + 1} / {totalCount}
            </Text>
          </Flex>
        )}


        {/* Company icon + name heading */}
        <HStack spacing={3} mb={4} align="center">
          <Flex w="36px" h="36px" borderRadius="8px" bg="whiteAlpha.100" align="center" justify="center" flexShrink={0}>
            <Building2 size={18} strokeWidth={1.5} color="rgba(255,255,255,0.5)" />
          </Flex>
          <Text fontSize="2xl" fontWeight="600" color="white" letterSpacing="-0.02em" lineHeight="1.2">
            {company.company_name || 'Untitled Company'}
          </Text>
        </HStack>

        {/* ───── Horizontal metadata row — all items in one flow, wraps naturally ───── */}
        <HStack spacing={0} flexWrap="wrap" rowGap={3} mb={0} align="flex-end">
          {/* Static meta items */}
          {metaItems.map((item, idx) => (
            <VStack key={idx} spacing={1} align="start" py={1} pr={6}>
              <HStack spacing={1.5}>
                <Box color="whiteAlpha.400"><item.icon size={12} strokeWidth={1.5} /></Box>
                <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">{item.label}</Text>
              </HStack>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500" pl="2px">{item.value}</Text>
            </VStack>
          ))}

          {/* Static link items */}
          {linkItems.map((item, idx) => (
            <VStack key={`link-${idx}`} spacing={1} align="start" py={1} pr={6}>
              <HStack spacing={1.5}>
                <Box color="whiteAlpha.400">
                  {item.iconComponent ? <Box as={item.iconComponent} boxSize="12px" /> : <item.icon size={12} strokeWidth={1.5} />}
                </Box>
                <Text fontSize="xs" color="whiteAlpha.400" fontWeight="500">{item.label}</Text>
              </HStack>
              <Link href={item.href} isExternal fontSize="xs" fontWeight="500" color={item.color}
                _hover={{ opacity: 0.8 }} onClick={(e) => e.stopPropagation()} pl="2px">
                {item.label === 'Website' ? new URL(item.href).hostname.replace('www.', '') : item.label}
              </Link>
            </VStack>
          ))}

        </HStack>

        {/* ───── Divider ───── */}
        <Box h="1px" bg="whiteAlpha.100" my={5} />

        {/* ───── Company Overview ───── */}
        {company.summary && (
          <VStack align="stretch" spacing={3} mb={6}>
            <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em">
              Company Overview
            </Text>
            <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.7" whiteSpace="pre-wrap" fontWeight="400">
              {company.summary}
            </Text>
          </VStack>
        )}

        {/* Sub-industry */}
        {company.sub_industry && company.industry && company.sub_industry !== company.industry && (
          <>
            <Box h="1px" bg="whiteAlpha.50" my={4} />
            <VStack align="stretch" spacing={3} mb={6}>
              <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em">
                Sub-Industry
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800" fontWeight="400">{company.sub_industry}</Text>
            </VStack>
          </>
        )}

        {/* Headquarters */}
        {company.headquarters && company.city && company.state && (
          <>
            <Box h="1px" bg="whiteAlpha.50" my={4} />
            <VStack align="stretch" spacing={3} mb={6}>
              <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em">
                Headquarters
              </Text>
              <Text fontSize="sm" color="whiteAlpha.800" fontWeight="400">{company.headquarters}</Text>
            </VStack>
          </>
        )}

        {detailFields.length > 0 && (
          <Text fontSize="sm" fontWeight="600" color="whiteAlpha.500" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
            Custom fields
          </Text>
        )}
        <DetailViewFieldsList
          fields={detailFields}
          dragIdx={dragIdx}
          dragOverIdx={dragOverIdx}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          renamingFieldId={renamingFieldId}
          renameValue={renameValue}
          renameInputRef={renameInputRef}
          onRenameChange={setRenameValue}
          onFinishRename={handleFinishRename}
          onCancelRename={() => { setRenamingFieldId(null); setRenameValue(''); }}
          onFieldContextMenu={(fieldId, x, y) => setContextMenu({ fieldId, x, y })}
          renderFieldValue={renderFieldValue}
          onAddField={openAddFieldModal}
        />

        {/* Bottom spacer */}
        <Box h="40px" />
      </Box>

      {/* ───── Context menu for dynamic fields ───── */}
      {contextMenu && (
        <Portal>
          <Box
            ref={contextMenuRef}
            position="fixed"
            left={`${contextMenu.x}px`}
            top={`${contextMenu.y}px`}
            bg="#0f0f0f"
            border="1px solid"
            borderColor="whiteAlpha.100"
            borderRadius="8px"
            boxShadow="0 8px 24px rgba(0,0,0,0.4)"
            py={1}
            zIndex={2000}
            minW="140px"
          >
            <Flex align="center" px={3} py={2} cursor="pointer"
              _hover={{ bg: 'whiteAlpha.100' }} transition="background 0.1s ease"
              onClick={() => {
                const field = detailFields.find(f => f.id === contextMenu.fieldId);
                if (field) handleStartRename(field);
              }}
            >
              <Box color="whiteAlpha.500" mr={2.5}><Pencil size={13} strokeWidth={1.5} /></Box>
              <Text fontSize="xs" color="whiteAlpha.800" fontWeight="500">Rename</Text>
            </Flex>
            <Flex align="center" px={3} py={2} cursor="pointer"
              _hover={{ bg: 'rgba(254,178,178,0.08)' }} transition="background 0.1s ease"
              onClick={() => handleDeleteField(contextMenu.fieldId)}
            >
              <Box color="red.400" mr={2.5}><Trash2 size={13} strokeWidth={1.5} /></Box>
              <Text fontSize="xs" color="red.400" fontWeight="500">Delete</Text>
            </Flex>
          </Box>
        </Portal>
      )}

      {/* ───── Research Field Modal (shared by both horizontal + vertical) ───── */}
      <Modal isOpen={isOpen} onClose={() => { onClose(); setModalMode('quick'); setCustomPrompt(''); }} isCentered size="md">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="#0a0a0a" border="1px solid" borderColor="rgba(255,255,255,0.10)" borderRadius="12px"
          boxShadow="0 24px 48px rgba(0,0,0,0.6)" mx={4} overflow="hidden">

          {/* ─── Shared header with pill toggle ─── */}
          <ModalHeader pb={0} pt={5} px={6}>
            <HStack spacing={2.5} mb={2.5}>
              <Flex w="28px" h="28px" borderRadius="7px" bg="rgba(255,255,255,0.08)" align="center" justify="center" flexShrink={0}>
                {modalMode === 'quick'
                  ? <Search size={14} strokeWidth={2} color="rgba(255,255,255,0.7)" />
                  : <Sparkles size={14} strokeWidth={2} color="rgba(255,255,255,0.7)" />}
              </Flex>
              <Text fontSize="md" fontWeight="600" color="#fafafa">
                Research {company.company_name}
              </Text>
            </HStack>

            {/* Pill toggle */}
            <Flex
              bg="#111111" borderRadius="10px" p="3px" w="100%"
              border="1px solid" borderColor="rgba(255,255,255,0.08)"
            >
              {[
                { key: 'quick', label: 'Single Field' },
                { key: 'custom', label: 'Deep Dive' },
              ].map((tab) => (
                <Flex key={tab.key} flex={1} align="center" justify="center"
                  py="6px" borderRadius="8px" cursor="pointer"
                  bg={modalMode === tab.key ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  transition="all 0.15s ease"
                  onClick={() => setModalMode(tab.key)}
                  _hover={undefined}
                >
                  <Text fontSize="xs" fontWeight="600"
                    color={modalMode === tab.key ? '#fafafa' : 'rgba(255,255,255,0.35)'}
                    transition="color 0.15s ease"
                  >
                    {tab.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,0.4)" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} top={4} right={4} />

          {/* ─── Quick Research Mode ─── */}
          {modalMode === 'quick' && (
            <ModalBody px={6} pb={6} pt={4}>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mb={4} lineHeight="1.5">
                Pick a template or describe your own — we'll search the web and bring back the answer.
              </Text>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>What do you want to find?</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>This becomes the label for the data point</Text>
                  <Input
                    placeholder="e.g., Ideal Customer Profile"
                    value={fieldName} onChange={(e) => setFieldName(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" h="40px"
                  />
                </Box>

                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>Describe your research question</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>Be specific — the more context you give, the better the answer</Text>
                  <Textarea
                    placeholder="e.g., Who are the company's ideal customers? What type of businesses do they typically serve?"
                    value={fieldDescription} onChange={(e) => setFieldDescription(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" rows={3} resize="none"
                  />
                </Box>

                <Button onClick={handleAddField} isDisabled={!fieldName.trim()}
                  bg="#fafafa" color="#0a0a0a" _hover={{ opacity: 0.85 }}
                  _disabled={{ bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)', cursor: 'not-allowed' }}
                  borderRadius="full" h="40px" fontSize="sm" fontWeight="600" w="100%" mt={1}
                  _focus={{ boxShadow: 'none' }}
                  leftIcon={<Search size={14} strokeWidth={2} />}
                >
                  Research
                </Button>
              </VStack>
            </ModalBody>
          )}

          {/* ─── Custom Prompt Mode ─── */}
          {modalMode === 'custom' && (
            <ModalBody px={6} pb={6} pt={4}>
              <Text fontSize="xs" color="rgba(255,255,255,0.4)" fontWeight="400" mb={4} lineHeight="1.5">
                Describe what you want to research — AI will determine the fields, data types, and structure automatically.
              </Text>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1.5}>What do you want to know?</Text>
                  <Text fontSize="xs" color="rgba(255,255,255,0.35)" mb={2}>
                    Be as detailed as you'd like — AI will break this into structured research fields
                  </Text>
                  <Textarea
                    placeholder="e.g., Research each company's recent funding rounds — I want the funding amount, round type, lead investors, and the date of the last round"
                    value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)}
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)" color="#fafafa" fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }} _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    borderRadius="8px" rows={5} resize="none"
                  />
                </Box>

                <Box px={3.5} py={2.5} borderRadius="8px" bg="rgba(255,255,255,0.02)" border="1px solid" borderColor="rgba(255,255,255,0.06)">
                  <HStack spacing={2} align="flex-start">
                    <Sparkles size={12} strokeWidth={2} color="rgba(255,255,255,0.35)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Text fontSize="xs" color="rgba(255,255,255,0.4)" lineHeight="1.6">
                      AI will analyze your prompt, determine how many fields to create, choose the right data types, and generate a research-optimized query.
                    </Text>
                  </HStack>
                </Box>

                <Button
                  onClick={handleDeepDive}
                  isLoading={deepDiveLoading}
                  loadingText="Analyzing..."
                  isDisabled={!customPrompt.trim() || deepDiveLoading}
                  bg="#fafafa" color="#0a0a0a" _hover={{ opacity: 0.85 }}
                  _disabled={{ bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)', cursor: 'not-allowed' }}
                  borderRadius="full" h="40px" fontSize="sm" fontWeight="600" w="100%" mt={1}
                  _focus={{ boxShadow: 'none' }}
                  leftIcon={!deepDiveLoading ? <Sparkles size={14} strokeWidth={2} /> : undefined}
                >
                  Generate Research Fields
                </Button>
              </VStack>
            </ModalBody>
          )}
        </ModalContent>
      </Modal>

      {/* ───── Delete Confirmation Modal ───── */}
      <Modal isOpen={!!deleteConfirm} onClose={() => !isDeleting && setDeleteConfirm(null)} isCentered size="sm">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="#0a0a0a" border="1px solid" borderColor="rgba(255,255,255,0.10)" borderRadius="12px"
          boxShadow="0 24px 48px rgba(0,0,0,0.6)" mx={4} overflow="hidden">
          <ModalHeader pb={0} pt={5} px={6}>
            <HStack spacing={2.5}>
              <Flex w="28px" h="28px" borderRadius="7px" bg="rgba(239,68,68,0.15)" align="center" justify="center" flexShrink={0}>
                <AlertTriangle size={14} strokeWidth={2} color="rgba(239,68,68,0.85)" />
              </Flex>
              <Text fontSize="md" fontWeight="600" color="#fafafa">Delete field</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="rgba(255,255,255,0.4)" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} top={4} right={4} />
          <ModalBody px={6} pb={6} pt={4}>
            <Text fontSize="sm" color="rgba(255,255,255,0.5)" lineHeight="1.6" mb={5}>
              <Text as="span" color="#fafafa" fontWeight="600">"{deleteConfirm?.label}"</Text>
              {' '}has data across{' '}
              <Text as="span" color="#fafafa" fontWeight="600">{deleteConfirm?.valueCount} companies</Text>.
              This will permanently remove the field and all its values.
            </Text>
            <HStack spacing={3} w="100%">
              <Button flex={1} size="sm" variant="ghost" color="rgba(255,255,255,0.5)"
                _hover={{ bg: 'rgba(255,255,255,0.06)', color: '#fafafa' }}
                _focus={{ boxShadow: 'none' }}
                borderRadius="full" h="36px" fontWeight="500"
                onClick={() => setDeleteConfirm(null)} isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button flex={1} size="sm"
                bg="rgba(239,68,68,0.15)" color="rgba(239,68,68,0.85)"
                border="1px solid rgba(239,68,68,0.25)"
                _hover={{ bg: 'rgba(239,68,68,0.25)' }}
                _focus={{ boxShadow: 'none' }}
                borderRadius="full" h="36px" fontWeight="600"
                leftIcon={!isDeleting ? <Trash2 size={13} strokeWidth={2} /> : undefined}
                isLoading={isDeleting} loadingText="Deleting..."
                onClick={() => executeDelete(deleteConfirm.fieldId)}
              >
                Delete for all
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      </Box>{/* end inner scrollable Box */}

      {/* Panel-edge navigation arrows */}
      {(onPrev || onNext) && (
        <>
          <Flex
            as="button"
            onClick={onPrev}
            disabled={!onPrev}
            position="absolute"
            left="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex={10}
            w="36px" h="56px"
            align="center"
            justify="center"
            bg="transparent"
            color={onPrev ? 'rgba(255,255,255,0.4)' : 'whiteAlpha.200'}
            cursor={onPrev ? 'pointer' : 'not-allowed'}
            _hover={onPrev ? { color: '#fafafa' } : {}}
            _active={{}}
          >
            <ChevronLeft size={18} strokeWidth={2} />
          </Flex>
          <Flex
            as="button"
            onClick={onNext}
            disabled={!onNext}
            position="absolute"
            right="0"
            top="50%"
            transform="translateY(-50%)"
            zIndex={10}
            w="36px" h="56px"
            align="center"
            justify="center"
            bg="transparent"
            color={onNext ? 'rgba(255,255,255,0.4)' : 'whiteAlpha.200'}
            cursor={onNext ? 'pointer' : 'not-allowed'}
            _hover={onNext ? { color: '#fafafa' } : {}}
            _active={{}}
          >
            <ChevronRight size={18} strokeWidth={2} />
          </Flex>
        </>
      )}
    </Box>
  );
};

export default CompanyDetailView;
